import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { reservationGuestShape } from '../reservation-guests/models'
import { bookingDetailEnum, providerKeyEnum, reservationKindEnum, statusKeyEnum } from './options'

export const receiptShape = {
  paysIva: z.boolean().optional(),
  // False until the booker answers the residency question `paysIva` is scored from
  ivaDetermined: z.boolean().optional(),
  // Whether the booker uploaded a passport entry stamp / any identity-document file
  hasEntryStamp: z.boolean().optional(),
  hasDocuments: z.boolean().optional(),
  total: z.number().optional(),
  preTax: z.number().optional(),
  ivaAmount: z.number().optional(),
  ivaRate: z.string().optional(),
  tipoDeDocumento: z.string().optional(),
  tipoDeFactura: z.string().optional(),
  tipoDeOperacion: z.string().optional(),
  numeracion: z.string().optional(),
  fechaDePago: z.string().nullable().optional(),
  busqueadaPorItem: z.string().optional(),
  cantidad: z.number().optional(),
  impuesto: z.string().optional(),
  tarifa: z.string().optional(),
  montoFinal: z.number().optional(),
}

export const reservationShape = {
  id: z.string().uuid(),
  created: z.string().datetime({ offset: true }),
  lastEdited: z.string().datetime({ offset: true }),
  checkinDate: z.string().datetime({ offset: true }),
  checkoutDate: z.string().datetime({ offset: true }),
  providerConfirmationCode: z.string().max(255).nullable().optional(),
  provider: z.enum(providerKeyEnum),
  status: z.enum(statusKeyEnum).optional(),
  kind: z.enum(reservationKindEnum).optional(),
  isPrivate: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  property: z.string().uuid(),
  reservationGuests: z.array(z.string().uuid()).optional(),
  reservationGuestsRef: z.array(z.object(reservationGuestShape)).optional(),
  totalPrice: z.number().or(z.string()).nullable().optional(),
  totalPriceCurrency: z.string(),
  guestPaidPrice: z.number().or(z.string()).nullable().optional(),
  guestPaidPriceCurrency: z.string(),
  needsSire: z.boolean().optional(),
  reservationErrors: z.array(z.string()).optional(),
  missingBookingDetails: z.array(z.enum(bookingDetailEnum)).optional(),
  declaredNumberOfGuests: z.number().optional(),
  receipt: z.object(receiptShape).optional(),
  reportsTra: z.boolean().optional(),
  reportsSire: z.boolean().optional(),
  propertyCountry: z.string().optional(),
  propertyTimezone: z.string().nullable().optional(),
}

export const reservationCreateShape = {
  checkinDate: z.string(),
  checkoutDate: z.string(),
  providerConfirmationCode: z.string().optional().nullable(),
  provider: z.enum(providerKeyEnum),
  kind: z.enum(reservationKindEnum).optional(),
  isPrivate: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  guests: z.array(z.string().uuid()).optional(),
  totalPrice: z.number().or(z.string()).nullable().optional(),
  totalPriceCurrency: z.string().optional(),
  guestPaidPrice: z.number().or(z.string()).nullable().optional(),
  guestPaidPriceCurrency: z.string().optional(),
  property: z.string().uuid(),
  declaredNumberOfGuests: z.number().optional(),
}

export const reservationGuestFromFormShape = {
  instanceId: z.string().uuid(),
  reservationId: z.string().uuid(),
}

export const reservationFiltersShape = {
  checkInView: z.string(),
  kind: z.string(),
  search: z.string(),
  property: z.string().uuid(),
  organization: z.string().uuid(),
}

export type ReservationGuestFromForm = GetInferredFromRaw<typeof reservationGuestFromFormShape>
export type Reservation = GetInferredFromRaw<typeof reservationShape>
export type Receipt = GetInferredFromRaw<typeof receiptShape>
export type ReservationCreateShape = GetInferredFromRaw<typeof reservationCreateShape>
export type ReservationFiltersShape = GetInferredFromRaw<typeof reservationFiltersShape>

export const quickAddGuestShape = {
  reservationId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().optional(),
  email: z.string().optional(),
  preferredLanguage: z.string().optional(),
}
export type QuickAddGuest = GetInferredFromRaw<typeof quickAddGuestShape>
